import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../shared/state/root-reducer';
import {
    setIsVOBModalOpen,
} from '../../../state/patient-record-locking-slice';
import {AppDispatch} from '../../../../shared/state/store';
import React, {useState, useEffect} from 'react';
import {Grid, IconButton} from '@mui/material';
import AccessControl from '../../../../security/components/access-control';
import {RolePageNames} from '../../../../security/model/role-page-names';
import EditIcon from '@mui/icons-material/Edit';
import {Vob as LocalVob} from '../../../../admissions-advisor/models/vob';
import {VOBSummaryV3} from '@finpay-development/shared-components';
import {FinpassVOBModal} from './finpass-vob-modal';
import {FacilityPayers, vobClassificationsItem} from '../../../../admissions-advisor/models/vob';
import {Vob, VobClassification} from '@finpay-estimation/estimation-types';
import {loadSelectedVob} from '../../../../shared/state/vob-thunk';
import {loadSelectedEstimate} from '../../../../shared/state/estimator-thunk';

interface VOBAccordionContentsProp {
    vobClassification?: any[] | null;
    facilityPayersState?: FacilityPayers[] | undefined;
}

// Default empty Vob object to use when selectedVob is null
const emptyVob: Vob = {
    patientId: 0,
    client: {
        clientId: 0,
        npi: "",
    },
    facility: {
        facilityId: 0,
        npi: "",
    },
    payor: {
        payorId: 0,
    },
    plan: {
        payorPlanId: 0,
    },
    vobSourceId: 0,
    isValid: false,
    digitalVerificationMethod: false,
    groupNum: "",
    policyNum: "",
    liveVOB: false,
    activePolicy: false,
    isCarryover: false,
    isCopayReset: false,
    policyBeginDate: "",
    policyEndDate: "",
};

// Helper function to map vobClassificationsItem to VobClassification
const mapToVobClassification = (classifications: vobClassificationsItem[] | null | undefined): VobClassification[] => {
    if (!classifications) return [];
    
    return classifications.map(item => ({
        vobClassificationId: item.vobClassificationId,
        vobClassificationName: item.vobClassificationName,
        vobClassificationDesc: item.vobClassificationDesc || undefined, // Convert null to undefined
        isInpatient: item.isInpatient || false,
        copayType: "No", // Default value required by VobClassification
        loc: [],
        notes: []
    }));
};

export const VerificationOfBenefitsAccordionContents = (
    props: VOBAccordionContentsProp
) => {
    const [isEditModal, setIsEditModal] = useState(false);
    const [isLoadingEstimate, setIsLoadingEstimate] = useState(false);

    const {vobClassification, facilityPayersState} = props;

    const state = {
        vob: useSelector(
            (state: RootState) => state.admissionsAdvisorContext?.vobContext.vob
        ),
        selectedPatientEncounter: useSelector(
            (state: RootState) => state.patientContext.selectedEncounter
        ),
        patientModalOpenStatus: useSelector(
            (state: RootState) =>
                state.patientRecordLockingContext.patientModalOpenStatus
        ),
        patientVOBState: useSelector(
            (state: RootState) => state.admissionsAdvisorContext.vobContext.vob
        ),
        // Add Vob state from shared/state/vob-slice.ts
        selectedVob: useSelector(
            (state: RootState) => state.vobContext.selectedVob
        ),
        patientVobList: useSelector(
            (state: RootState) => state.vobContext.patientVobList
        ),
        isVobLoading: useSelector(
            (state: RootState) => state.vobContext.isLoading
        ),
        vobError: useSelector(
            (state: RootState) => state.vobContext.error
        ),
        // Add estimate state to get vobId
        estimate: useSelector(
            (state: RootState) => state.estimatorContext.selectedEstimate
        ),
        estimateLoading: useSelector(
            (state: RootState) => state.estimatorContext.isLoading.selectedEstimate
        ),
        masterVobClassifications: useSelector(
            (state: RootState) => state.vobContext.vobClassifications
        ),
    };

    const {
        vob,
        selectedPatientEncounter,
        patientModalOpenStatus,
        patientVOBState,
        selectedVob,
        patientVobList,
        isVobLoading,
        vobError,
        estimate,
        estimateLoading,
        masterVobClassifications,
    } = state;

    const dispatch = useDispatch<AppDispatch>();

    // First, load the estimate if needed
    useEffect(() => {
        // Check if we have a selected patient encounter but no estimate or if the estimate doesn't match the encounter
        if (selectedPatientEncounter?.pfrEstimateId && 
            (!estimate || estimate.estimateId !== selectedPatientEncounter.pfrEstimateId) && 
            !isLoadingEstimate && 
            !estimateLoading) {
            
            console.log('Loading estimate for encounter:', selectedPatientEncounter.pfrEstimateId);
            setIsLoadingEstimate(true);
            
            // Load the estimate for the selected encounter
            dispatch(loadSelectedEstimate({
                paramId: -2,
                estimationId: selectedPatientEncounter.pfrEstimateId
            }))
            .unwrap()
            .then((estimateData) => {
                console.log('Estimate loaded successfully:', estimateData);
                setIsLoadingEstimate(false);
            })
            .catch((error) => {
                console.error('Error loading estimate:', error);
                setIsLoadingEstimate(false);
            });
        }
    }, [selectedPatientEncounter, estimate, dispatch, estimateLoading, isLoadingEstimate]);

    // Then, load the VOB based on the estimate's vobId
    useEffect(() => {
        // Check if we have an estimate with vobId and no selectedVob
        if (estimate?.vobId && !selectedVob && !isVobLoading.selectedVob) {
            console.log('Loading VOB from estimate with vobId:', estimate.vobId);
            
            // Call the loadSelectedVob thunk which uses getVobV3 and properly sets the selectedVob
            dispatch(loadSelectedVob({ vobId: estimate.vobId }))
                .unwrap()
                .then(() => {
                    console.log('VOB loaded successfully');
                })
                .catch((error) => {
                    console.error('Error loading VOB:', error);
                });
        }
    }, [estimate, selectedVob, dispatch, isVobLoading.selectedVob]);

    const handleVOBModalClose = () => {
        dispatch(setIsVOBModalOpen(false));
    };

    const handleEditModalClick = async (vob: LocalVob) => {
        dispatch(setIsVOBModalOpen(true));
        setIsEditModal(true);
    };

    const isInNetwork = estimate?.rateAsPayorPlan?.payor?.inNetwork ?? selectedPatientEncounter?.payorRisk?.payorRiskId === 1;
    
    // Use selectedVob if available, otherwise use the empty Vob object
    const vobToDisplay = selectedVob || emptyVob;

    // Convert the masterVobClassifications to the expected VobClassification type
    const mappedClassifications = mapToVobClassification(masterVobClassifications);

    return (
        <>
            <Grid container>
                {!selectedPatientEncounter?.isConverted && (
                    <Grid item container justifyContent="flex-end">
                        <AccessControl
                            rolePageName={RolePageNames.PatientRecords}
                            actionName="Add Instance of Care"
                            renderNoAccess={() => {
                                return (
                                    <IconButton disabled size="large">
                                        <EditIcon className="colorDisabled" />
                                    </IconButton>
                                );
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <IconButton
                                    onClick={() => handleEditModalClick(vob)}
                                    size="large"
                                >
                                    <EditIcon className="icon" />
                                </IconButton>
                            </div>
                        </AccessControl>
                    </Grid>
                )}
                <VOBSummaryV3 
                    vob={vobToDisplay} 
                    isInNetwork={isInNetwork}
                    classifications={mappedClassifications}
                />
            </Grid>
            {vobClassification && patientModalOpenStatus.isVOBModalOpen && (
                <FinpassVOBModal
                    open={patientModalOpenStatus?.isVOBModalOpen}
                    isEdit={isEditModal}
                    classifications={vobClassification}
                    facilityPayersState={facilityPayersState}
                    handleVOBModalCancel={handleVOBModalClose}
                    handleVOBModalSubmit={handleVOBModalClose}
                />
            )}
        </>
    );
};
